import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import { XRayAnalyticsType } from '@mm/state/controllers/BotAnalyticsController';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { AnalyticsFilterByDate } from '../BotAnalytics/AnalyticsFilterByTime/AnalyticsFilterByDate';
import { XRayContactsLineChart } from './AnalyticsCharts/XRayContactsLineChart';
import { XRayContactsPieChart } from './AnalyticsCharts/XRayContactsPieChart';
import styles from './XRayAnalytics.module.css';
import { XRayContactsByLandingPage } from './XRayContactsByLandingPage/XRayContactsByLandingPage';

export const XRayAnalytics = () => {
  const [startDate, setStartDate] = useState<moment.Moment | null>(
    moment().subtract(30, 'days'),
  );
  const [busy, setBusy] = useState<XRayAnalyticsType[]>([]);
  const [endDate, setEndDate] = useState<moment.Moment | null>(moment());
  const [dateRangeMode, setDateRangeMode] = useState<string | null>(
    'Last 30 days',
  );

  const botId = useSelector(activeChatbotIdSelector);
  const { retrieveXRayAnalytics } = useBotAnalyticsCRUDActions();
  const history = useHistory();

  const navigateToSuppressions = () =>
    history.push(BotBuilderRoutes.integrations.getLink({ chatbotId: botId }), {
      kind: 'contact-suppression',
    });

  const pieChartFooter = (
    <div className={styles.pieFooter}>
      A max. of 15 domains are shown
      <div onClick={navigateToSuppressions} className={styles.link}>
        Suppress email providers you do not want
      </div>
    </div>
  );

  const xRayAnalyticsConstants: XRayAnalyticsType[] = [
    'contacts_gathered',
    'email_sent',
    'email_domain',
    'wday',
    'top_urls',
  ];

  const fetchData = async () => {
    const formattedStartDate = startDate?.format('YYYY-MM-DD');
    const formattedEndDate = endDate?.format('YYYY-MM-DD');
    const params = {
      botId,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
    xRayAnalyticsConstants.forEach(async type => {
      setBusy(state => [...state, type]);
      await retrieveXRayAnalytics(type, params);
      setBusy(state => state.filter(t => t !== type));
    });
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        X-Ray Pixel Analytics
        <div className={styles.calendarContainer}>
          View by date
          <AnalyticsFilterByDate
            className={styles.calendar}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateRangeMode={dateRangeMode}
            setDateRangeMode={setDateRangeMode}
          />
        </div>
      </div>
      <XRayContactsLineChart
        busy={busy.includes('contacts_gathered') || busy.includes('email_sent')}
      />
      <div className={styles.chartsRow}>
        <XRayContactsByLandingPage busy={busy.includes('top_urls')} />
        <XRayContactsPieChart
          title="Contacts by email provider"
          type="email_domain"
          footer={pieChartFooter}
          busy={busy.includes('email_domain')}
          withLegend
        />
        <XRayContactsPieChart
          title="Contacts by day of the week"
          type="wday"
          busy={busy.includes('wday')}
        />
      </div>
    </div>
  );
};
